import type { HandlersFullReleaseWaitlistSignupRequest } from '~/api/models/HandlersFullReleaseWaitlistSignupRequest';
import type { HandlersFullReleaseWaitlistSignupResponse } from '~/api/models/HandlersFullReleaseWaitlistSignupResponse';
import { HandlersFullReleaseWaitlistSignupResponseFromJSON } from '~/api/models/HandlersFullReleaseWaitlistSignupResponse';
import { marketingBackendApiUrl, marketingBackendUrl } from '../config';
import { sanitizeUserFacingMessage } from '../siteDown';
import {
  parseMarketingApiJsonBody,
  WAITLIST_UNAVAILABLE_MESSAGE,
} from '../utils/upstreamHttpResponse';
import { consumeWaitlistSignupSource } from './waitlistSignupSource';

/** Backend max ~4 KiB for metadata JSON — keep UTM + path only. */
const METADATA_CAP = 4000;

/**
 * Door campaign id, set by MarketingPage after its route-prop-wins resolution. Re-deriving from
 * the URL here would miss the /for-him,/for-her,/open-minded routes entirely and could disagree
 * with the page on mismatched links (route prop beats ?door=).
 */
/** Set per submission by submitFullReleaseWaitlist, read by collectMetadata. */
let pendingCityOther = '';

let waitlistDoor: string | null = null;
export function setWaitlistDoor(door: string | null): void {
  waitlistDoor = door;
}

/** The resolved door, for analytics that must be comparable across the three campaign landings. */
export function getWaitlistDoor(): string | null {
  return waitlistDoor;
}

export function fullReleaseWaitlistEnabled(): boolean {
  return marketingBackendUrl().length > 0;
}

function collectMetadata(): Record<string, unknown> {
  if (typeof window === 'undefined') {
    return {};
  }
  const m: Record<string, unknown> = { path: window.location.pathname };
  const sp = new URLSearchParams(window.location.search);
  for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
    const v = sp.get(k);
    if (v) m[k] = v;
  }
  const ref = sp.get('ref');
  if (ref) m.ref = ref;
  // An unlisted metro, kept countable for deciding which city opens next without polluting the
  // canonical slug the notify job groups on.
  if (pendingCityOther) m.cityOther = pendingCityOther;
  // Door campaign attribution — the resolved door from the page (route prop or ?door=).
  if (waitlistDoor) m.door = waitlistDoor;
  let s = JSON.stringify(m);
  if (s.length > METADATA_CAP) {
    // Keep the two fields the campaign cannot lose; UTM noise is what gets dropped.
    return { path: m.path, ...(waitlistDoor ? { door: waitlistDoor } : {}) };
  }
  return m;
}

function parseWaitlistSignupJson(json: unknown): HandlersFullReleaseWaitlistSignupResponse {
  const data = HandlersFullReleaseWaitlistSignupResponseFromJSON(json);
  const o = json as Record<string, unknown> | null;
  if (o?.already_registered === true) {
    data.alreadyRegistered = true;
  }
  return data;
}

/** Backend may return 200 + alreadyRegistered, or 409 / error codes when duplicates are rejected. */
function isDuplicateWaitlistSignup(res: Response, json: unknown): boolean {
  if (res.status === 409) return true;
  const o = json as Record<string, unknown> | null;
  if (!o) return false;
  if (o.alreadyRegistered === true || o.already_registered === true) return true;
  const err = o.error as Record<string, unknown> | undefined;
  const code = String(err?.code ?? o.code ?? '')
    .trim()
    .toUpperCase();
  if (!code) return false;
  return (
    code === 'ALREADY_REGISTERED' ||
    code === 'EMAIL_ALREADY_REGISTERED' ||
    code === 'DUPLICATE_EMAIL' ||
    code === 'ALREADY_EXISTS' ||
    (code.includes('ALREADY') && (code.includes('REGISTER') || code.includes('WAITLIST')))
  );
}

export async function submitFullReleaseWaitlist(params: {
  email: string;
  firstName?: string;
  citySlug?: string;
  /** Free-text metro when citySlug is 'other'. Rides in metadata; never becomes the slug. */
  cityOther?: string;
  source?: string;
}): Promise<
  { ok: true; data: HandlersFullReleaseWaitlistSignupResponse } | { ok: false; message: string }
> {
  const endpoint = marketingBackendApiUrl('/api/waitlist/full-release');
  if (!endpoint) {
    return {
      ok: false,
      message: 'Waitlist is not configured (set VITE_MARKETING_BACKEND_URL).',
    };
  }

  const email = params.email.trim().toLowerCase();
  // collectMetadata reads this, so it has to be set before the body is built.
  pendingCityOther = params.cityOther?.trim() || '';
  const body: HandlersFullReleaseWaitlistSignupRequest = {
    email,
    firstName: params.firstName?.trim() || undefined,
    citySlug: params.citySlug?.trim().toLowerCase() || undefined,
    source: params.source?.trim() || consumeWaitlistSignupSource(),
    metadata: collectMetadata(),
  };
  if (!email) {
    return { ok: false, message: 'Email is required.' };
  }

  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const raw = await res.text();
  const parsed = parseMarketingApiJsonBody(raw, WAITLIST_UNAVAILABLE_MESSAGE);
  if (!parsed.ok) {
    return { ok: false, message: parsed.userMessage };
  }
  const json = parsed.json;

  if (!res.ok) {
    if (isDuplicateWaitlistSignup(res, json)) {
      return {
        ok: true,
        data: { ok: true, alreadyRegistered: true, created: false },
      };
    }
    const err = (json as { error?: { message?: string }; message?: string })?.error;
    const msg =
      typeof err?.message === 'string'
        ? err.message
        : typeof (json as { message?: string })?.message === 'string'
          ? (json as { message: string }).message
          : `Request failed (${res.status})`;
    return {
      ok: false,
      message: sanitizeUserFacingMessage(msg, WAITLIST_UNAVAILABLE_MESSAGE),
    };
  }

  const data = parseWaitlistSignupJson(json);
  if (data.ok === false && data.alreadyRegistered !== true) {
    return {
      ok: false,
      message: sanitizeUserFacingMessage(
        'Could not add you to the waitlist. Please try again.',
        WAITLIST_UNAVAILABLE_MESSAGE
      ),
    };
  }

  return { ok: true, data };
}
